import type { FollowUpState, QueueItem } from "@/lib/api/ghl-types"

// The follow-up state in the shop's words — the same vocabulary as the
// Customers page and the Tasks room, so nobody learns a second one.

export const FOLLOW_UP: Record<FollowUpState, { label: string; dot: string; text: string }> = {
  to_confirm: { label: "Estimate to confirm", dot: "bg-amber-500", text: "text-amber-700" },
  waiting: { label: "Waiting on you", dot: "bg-zinc-900", text: "text-zinc-900" },
  to_call: { label: "Call due", dot: "bg-amber-500", text: "text-amber-700" },
  following: { label: "Following up", dot: "bg-blue-500", text: "text-zinc-700" },
  booked: { label: "Booked", dot: "bg-sky-500", text: "text-zinc-700" },
  won: { label: "Won", dot: "bg-emerald-500", text: "text-emerald-700" },
  passed: { label: "Passed", dot: "bg-zinc-300", text: "text-zinc-500" },
  no_contact: { label: "No contact info", dot: "bg-red-500", text: "text-red-700" },
  idle: { label: "No open deal", dot: "bg-zinc-300", text: "text-zinc-500" },
}

export const SEGMENTS: Array<{ key: string; label: string; states: FollowUpState[] }> = [
  { key: "all", label: "All", states: [] },
  { key: "to_confirm", label: "To confirm", states: ["to_confirm"] },
  { key: "waiting", label: "Waiting on you", states: ["waiting"] },
  { key: "to_call", label: "To call", states: ["to_call"] },
  { key: "following", label: "Following up", states: ["following"] },
  { key: "closed", label: "Booked & won", states: ["booked", "won"] },
  { key: "no_contact", label: "No contact info", states: ["no_contact"] },
  { key: "passed", label: "Passed", states: ["passed", "idle"] },
]

/** Suggested one-tap replies, from what they wrote. */
export function suggestedReplies(lastInbound: string | null | undefined, first: string): string[] {
  const s = (lastInbound ?? "").toLowerCase()
  const out: string[] = []
  if (/loaner|rental|ride/.test(s)) out.push("Let me check on a loaner for you and text you right back.")
  if (/insurance|claim|adjuster|deductible/.test(s)) out.push("Yes, that is the estimate for the claim. Happy to walk you through it.")
  if (/how long|days|take/.test(s)) out.push("Let me confirm the timing with the shop and get back to you today.")
  if (/friday|monday|tuesday|wednesday|thursday|saturday|morning|afternoon|next week|come in|book/.test(s)) out.push("That works — I will hold it for you. Reply C to confirm and I will text the details.")
  if (/husband|wife|partner|think|later|get back/.test(s)) out.push("Of course. We are here when you are ready.")
  if (/price|expensive|cost|cheaper/.test(s)) out.push("Happy to go through the numbers on a quick call — what time suits you?")
  out.push(`Hi ${first}, following up on your estimate — happy to answer anything.`)
  out.push("Want me to hold a slot for you this week?")
  return [...new Set(out)].slice(0, 4)
}

export const CALLBACK_PRESETS = [
  { label: "Tomorrow", days: 1 },
  { label: "In 2 days", days: 2 },
  { label: "Next week", days: 5 },
] as const

export const DECLINE_REASONS: Array<{ value: "price" | "another_shop" | "insurance_total_loss" | "not_ready" | "no_longer_needed" | "unreachable" | "other"; label: string }> = [
  { value: "price", label: "Price" },
  { value: "another_shop", label: "Went elsewhere" },
  { value: "insurance_total_loss", label: "Insurance total loss" },
  { value: "not_ready", label: "Not ready yet" },
  { value: "no_longer_needed", label: "No longer needed" },
  { value: "unreachable", label: "Can't reach them" },
  { value: "other", label: "Other" },
]

export function queueKindWord(item: QueueItem): string {
  switch (item.kind) {
    case "reply": return "Reply"
    case "confirm": return "Confirm"
    case "call": return item.attempt && item.attempt > 1 ? `Call · try ${item.attempt}` : "First call"
    case "callback": return "Callback"
    case "wrapup": return "Last call"
    case "visit": return "Visit"
    default: return "Task"
  }
}
